import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Copy, ExternalLink, BadgeCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import { PROFILE } from "@/data";
import { Reveal, SectionHeading } from "@/components/Reveal";

export default function VerifiedBadge() {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onMove = (e) => {
    const r = cardRef.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 8, ry: px * 10 });
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(PROFILE.freelancerId);
      toast.success("Freelancer ID copied to clipboard");
    } catch {
      toast.error("Could not copy — long-press to copy manually");
    }
  };

  return (
    <section id="verified" data-testid="verified-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading id="verified" overline="Trust / 01" title="Government Verified Identity" />
        <div style={{ perspective: 1200 }} className="max-w-3xl">
          <Reveal>
            <motion.div
              ref={cardRef}
              onMouseMove={onMove}
              onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
              animate={{ rotateX: tilt.rx, rotateY: tilt.ry }}
              transition={{ type: "spring", stiffness: 180, damping: 20 }}
              className="relative overflow-hidden rounded-2xl border border-[#D9A441]/25 bg-gradient-to-br from-[#101F38] via-[#0D1B2E] to-[#101F38] shadow-xl shadow-black/30 p-8 sm:p-10"
              data-testid="verified-card"
            >
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-[#D9A441]/10 to-transparent animate-shimmer" />
              </div>

              <div className="relative flex flex-col sm:flex-row sm:items-center gap-8">
                <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
                  <BadgeCheck size={38} className="text-emerald-400" />
                </div>
                <div className="flex-1">
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-dot" />
                    Verified · freelancers.gov.bd
                  </p>
                  <p className="font-display text-2xl sm:text-3xl font-bold text-[#F3EFE6] tracking-tight">{PROFILE.name}</p>
                  <p className="mt-3 font-mono text-lg sm:text-xl text-[#D9A441] tracking-widest" data-testid="freelancer-id-text">
                    ID · {PROFILE.freelancerId}
                  </p>
                  <p className="mt-2 text-sm text-[#9DAEC4]">Issued 21 Jan 2026 · Valid until 20 Jan 2029</p>
                </div>
              </div>

              <div className="relative mt-8 flex flex-wrap gap-4">
                <button
                  onClick={copyId}
                  data-testid="btn-copy-freelancer-id"
                  className="flex items-center gap-2 bg-[#D9A441] hover:bg-[#E5B85C] text-[#0A1730] font-bold px-6 py-3 rounded-full transition-all duration-300 hover:scale-[1.03]"
                >
                  <Copy size={16} />
                  Copy ID
                </button>
                <a
                  href={PROFILE.idCardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="btn-view-id-card"
                  className="flex items-center gap-2 border border-[#D9A441]/40 hover:border-[#D9A441] text-[#D9A441] font-semibold px-6 py-3 rounded-full transition-all duration-300 hover:scale-[1.03]"
                >
                  <FileText size={16} />
                  View ID Card
                </a>
              </div>
            </motion.div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
